import { NgZone } from '@angular/core';
import { ofType } from '@mode/legacy/util-domain';
import { EmbedActionType, EmbedReportActions, ReportBuilderStore } from '@mode/reports/sandbox/contract';
import { CappieReporterTypes, FeatureFlag, FeatureFlagsFacade } from '@mode/shared/contract-common';
import { CappieReporterService } from '@mode/shared/util-cappie-reporter';
import { extractScripts, tokenFromVizID } from '@mode/shared/util-js';
import * as angular from 'angular';
import * as $ from 'jquery';
import { intersection } from 'lodash';
import { Observable, Subject, combineLatest, fromEvent, partition } from 'rxjs';
import { debounceTime, filter, first, map, scan, switchMap, takeUntil, withLatestFrom } from 'rxjs/operators';
import { Actions } from '~/actions';
import { LayoutSelectors } from '~/modules/embed-report/domain/layout/embed-layout.selectors';
import { VIZ_SELECTORS } from '../../../common/constants';
import { VizTypes } from '../../../common/types';
import * as LegacyEmbedDatasetSelectors from '../domain/datasets/embed-datasets.selectors';
import { EmbedReportSelectors } from '../domain/embed-report.selectors';
import { loadCustomScripts } from '../services/custom-js.service';

export class EmbedViewerLayoutController implements angular.IController {
  static $inject = [
    '$window',
    '$scope',
    '$element',
    '$compile',
    'RxStoreService',
    'actions$',
    'CappieReporterService',
    'FeatureFlagsFacade',
    'NgZone',
  ];

  private _renderingDone$ = new Subject<void>();

  initialLayout: string;

  $layoutScope: angular.IScope;

  isCaptureMode = false;

  $layout: angular.IAugmentedJQuery;

  vizScopes: { [token: string]: angular.IScope };

  fullWidthReport$: Observable<boolean>;

  virtualized: boolean; //if virtualized flag is set

  observer: IntersectionObserver;

  visualizationsBeingRendered: Set<string> = new Set();

  visualizationsRendered = 0;

  totalVisualizations = 0;

  reportRenderStatus: CappieReporterTypes.ProgressEvents;
  frameInitReporter: CappieReporterTypes.ProgressEvents;

  constructor(
    private $window: angular.IWindowService,
    private $scope: angular.IScope,
    private $element: angular.IAugmentedJQuery,
    private $compile: angular.ICompileService,
    private rxStore: ReportBuilderStore,
    private actions$: Observable<Actions>,
    private cappieReporterService: CappieReporterService,
    private featureFlagsFacade: FeatureFlagsFacade,
    private ngZone: NgZone
  ) {
    this.vizScopes = {};

    this.fullWidthReport$ = rxStore.select(EmbedReportSelectors.fullWidth);
    this.frameInitReporter = this.cappieReporterService.progressEventAPI('iframe_init');
    this.frameInitReporter.start('iframe: starting');
  }

  $onInit() {
    this.reportRenderStatus = this.cappieReporterService.progressEventAPI('mode-report-rendering');
    combineLatest([
      this.rxStore.select(EmbedReportSelectors.chartVizTokens),
      this.rxStore.select(EmbedReportSelectors.notebookVizTokens),
      this.rxStore.select(EmbedReportSelectors.tableVizTokens),
    ])
      .pipe(takeUntil(this._renderingDone$))
      .subscribe(([chartTokens, notebookTokens, tableTokens]) => {
        this.computeNumberOfVizToRender([...chartTokens, ...notebookTokens, ...tableTokens]);
      });

    const { load, fragment } = extractScripts(this.initialLayout, this.$window.document.head);
    this.$layout = $('<div></div>').append(fragment);

    this.$layoutScope = this.$scope.$new(true);
    const $header = this.$layout.find('.mode-header');

    $header.replaceWith(this.$compile($header)(this.$layoutScope));
    this.$element.append(this.$layout.children());
    this.$layout = this.$element;

    this.shouldShowReportTitle();

    /**
     * Fire an event if the user scrolls the viewport. This information affects
     * which virtualized render event is sent in the trace.
     */
    fromEvent(this.$layout, 'scroll')
      .pipe(
        // This event may still fire even if the user doesn't scroll, so check the position
        filter((event) => (event.currentTarget as HTMLElement).scrollTop > 0),
        first()
      )
      .subscribe((event) => {
        this.rxStore.dispatch(new EmbedReportActions.ReportHasScrolled());
      });

    const intersection$ = new Subject<IntersectionObserverEntry[]>();
    intersection$
      .pipe(
        scan(
          (acc, next) => {
            next.forEach((entry) => {
              const token = tokenFromVizID(entry.target.id);
              if (token && entry.target.tagName === 'MODE-CHART' && this.vizScopes[token] != null) {
                acc[token] = entry.isIntersecting;
              }
            });

            return acc;
          },
          {} as Record<string, boolean>
        ),
        debounceTime(50) // intersection updates create a lot of work for the main app, so avoid spamming them
      )
      .subscribe((intersections) => {
        this.rxStore.dispatch(new EmbedReportActions.UpdateVizIntersections(intersections));
        this.$scope.$evalAsync();
      });

    this.observer = new IntersectionObserver(
      (entries) => {
        if (this.virtualized) {
          intersection$.next(entries);
        }
      },
      {
        root: this.$element[0],
        rootMargin: '500px 0px 500px 0px',
      }
    );

    // Update the header when needed
    this.rxStore.select(EmbedReportSelectors.reportHeader).subscribe((header) => {
      this.$layoutScope.$evalAsync(() => {
        this.$layoutScope.title = header.name;
        this.$layoutScope.description = header.description;
      });
    });

    // Wait for UpdateState to ensure we have the full state before checking for imported datasets
    const hasImportedDatasets$ = this.actions$.pipe(
      ofType(EmbedReportActions.Type.UpdateState), // waiting for UpdateState allows us to avoid flip-flopping emitted values as state arrives
      first(),
      switchMap(() => this.rxStore.select(LegacyEmbedDatasetSelectors.hasImportedDatasetsReliable))
    );

    const [willSkipCustomJs, shouldExecuteCustomJS] = partition(hasImportedDatasets$, (hasImportedDataset) => {
      if (hasImportedDataset) {
        this.frameInitReporter.progress('has imported datasets, will skip customJS');
      } else {
        this.frameInitReporter.progress('no imported datasets, will wait for datasets then execute customJS');
      }
      return hasImportedDataset; // will skip custom js when this is true, else wait for datasets then apply
    });

    // Currently we'll never apply custom js if there's an imported dataset.
    // Update cappie if it will never happen.
    willSkipCustomJs.subscribe(() => this.frameInitReporter.end('iframe: customJS skipped'));

    // setup datasets for custom scripts
    const datasetsLoaded$ = shouldExecuteCustomJS.pipe(
      switchMap(() => {
        this.frameInitReporter.progress('iframe: checking if datasets loaded for custom scripts');
        return this.rxStore.select(LegacyEmbedDatasetSelectors.legacyEmbedDatasetsLoadedForAllRunQueries);
      })
    );

    datasetsLoaded$
      .pipe(
        filter((loaded) => {
          if (loaded) {
            this.frameInitReporter.progress('iframe: all datasets loaded, proceeding with custom scripts');
          } else {
            this.frameInitReporter.progress('iframe: datasets not yet loaded, waiting...');
          }
          return loaded;
        }),
        switchMap(() =>
          this.rxStore
            .select(LegacyEmbedDatasetSelectors.legacyEmbedDatasets)
            .pipe(
              withLatestFrom(
                this.rxStore.select(LegacyEmbedDatasetSelectors.legacyEmbedDatasetsForLocalQueriesOrderedByCreationDate)
              ),
              takeUntil(datasetsLoaded$.pipe(filter((l) => !l)))
            )
        )
      )
      .subscribe(([datasets, orderedDatasets]) => {
        this.frameInitReporter.progress('iframe: executing loadCustomScripts');
        // Execute customjs code outside of our Angular zone so we don't report errors
        // Alamode has it's own manual reporting mechanism
        this.ngZone.runOutsideAngular(() =>
          loadCustomScripts(this.$window, load, datasets, orderedDatasets).then(() => {
            this.frameInitReporter.end('iframe: customJS loaded');
          })
        );
      });

    this.rxStore
      .select(EmbedReportSelectors.reportLayout)
      .pipe(
        filter((layout) => layout != null),
        switchMap(() =>
          combineLatest([
            this.featureFlagsFacade.asObservable(FeatureFlag.EnableCustomReportVirtualization),
            this.rxStore.select(LayoutSelectors.hasCustomStyle),
            this.rxStore.select(LayoutSelectors.hasCustomScripts),
            this.rxStore.select(LayoutSelectors.disableVirtualScroll),
          ])
        ),
        map(
          ([isVirtualDefault, hasCustomStyle, hasCustomScripts, disableVirtualScroll]) =>
            !this.isCaptureMode && !disableVirtualScroll && (isVirtualDefault || (!hasCustomStyle && !hasCustomScripts))
        )
      )
      .subscribe((virtualized) => {
        if (!virtualized) {
          // If they're opted out, need to ensure the exclusion class is added.
          this.$layout.find('.mode-grid').addClass('no-virtual-scroll');

          // mark all charts as visible to emulate no virtualization, otherwise chart data won't be loaded
          const intersections = this.$layout
            .find('mode-chart')
            .toArray()
            .reduce(
              (acc, elem) => {
                acc[elem.id.split('_')[1]] = true;
                return acc;
              },
              {} as Record<string, boolean>
            );
          this.rxStore.dispatch(new EmbedReportActions.UpdateVizIntersections(intersections));
        }
        this.virtualized = virtualized;
        // eslint-disable-next-line no-console
        console.log(`Virtualized rendering for this report is ${virtualized ? 'ON' : 'OFF'}`);
        this.rxStore.dispatch(new EmbedReportActions.ReportStartRendered(virtualized));
        this.compileVisuals();
      });

    // To safely reset, wipe out all existing scopes
    this.actions$.pipe(ofType(EmbedReportActions.Type.ResetState)).subscribe(() => {
      this.observer.disconnect();
      for (const token in this.vizScopes) {
        if (this.vizScopes.hasOwnProperty(token) && this.vizScopes[token] != null) {
          this.vizScopes[token].$destroy();
          delete this.vizScopes[token];
        }
      }

      this.compileVisuals();
    });

    this.actions$
      .pipe(
        ofType(
          EmbedActionType.ChartRendered,
          EmbedActionType.NotebookVisualizationRendered,
          EmbedActionType.TableRendered
        )
      )
      .subscribe(({ token }) => {
        if (this.visualizationsBeingRendered.has(token)) {
          this.visualizationsRendered++;
          this.visualizationsBeingRendered.delete(token);
          this.reportRenderStatus.progress(
            `${this.visualizationsRendered} of ${this.totalVisualizations} vizs rendered.`
          );
          if (this.totalVisualizations === this.visualizationsRendered) {
            this.rxStore.dispatch({
              type: EmbedReportActions.Type.ReportRendered,
            });
            this.reportRenderStatus.end(`${this.visualizationsRendered} of ${this.totalVisualizations} vizs rendered.`);
            this._renderingDone$.next();
          }
        }
      });

    this.featureFlagsFacade
      .featureValueAsObservable<number>(FeatureFlag.LongCappieWorkEvent)
      .pipe(first())
      .subscribe((delay) => {
        if (delay) {
          this.cappieReporterService.delayCapture(delay);
        }
      });
  }

  compileVisuals() {
    const $visuals = this.$layout.find(VIZ_SELECTORS);
    const existingVizIds = $visuals.toArray().map((e) => e.id.split('_') as [VizTypes, string]);

    // Compile visuals that are in the layout. Should only happen on the first call, since everything after should be add/delete
    const uncompiledTokens = existingVizIds.filter(([_, token]) => this.vizScopes[token] == null);
    uncompiledTokens.forEach(([type, token]) => {
      // Don't add the token to the map since it's not a visualization and this will prevent the page to be considered rendered
      // We cannot filter this type because the component needs to be compile in order to render with the proper styles
      if (type !== VizTypes.Text) {
        this.visualizationsBeingRendered.add(token);
      }

      const $vizToCompile = $visuals.filter(`#${type}_${token}`);
      if ($vizToCompile.length > 0) {
        if (this.vizScopes[token] == null) {
          // might be safer to check
          this.vizScopes[token] = this.$scope.$new(true);
          this.vizScopes[token]['virtualized'] = this.virtualized;
        }
        const compiled = this.$compile($vizToCompile)(this.vizScopes[token]);
        $vizToCompile.replaceWith(compiled);
        this.observer.observe(compiled[0]);
      }
    });

    if (this.visualizationsBeingRendered.size > 0) {
      this.reportRenderStatus.start(`charts to render: ${this.visualizationsBeingRendered.size}`);
    }
  }

  private shouldShowReportTitle() {
    if (this.isCaptureMode) {
      this.$layout.find('.embed-hidden').removeClass('embed-hidden');
    }
  }

  private computeNumberOfVizToRender(vizTokens: string[]) {
    if (this.visualizationsBeingRendered.size > 0) {
      this.totalVisualizations = intersection(Array.from(this.visualizationsBeingRendered), vizTokens).length;
    }
  }
}
